/**
 * Helpers for displaying and computing skein counts that may include
 * a fractional leftover (1/4, 1/2, 3/4 of a skein).
 *
 * `Yarn.skeinCount` is the whole-skein count; `Yarn.partialSkein` is
 * the optional fractional remainder. The total quantity a user owns
 * of a yarn is `skeinCount + (partialSkein || 0)`.
 *
 * Inventory math (available / in-use / over-allocated) should use
 * `totalOwnedSkeins(yarn)` rather than reading `skeinCount` directly,
 * so the partial is not silently dropped.
 */

/**
 * Everything the user owns of a yarn, on the skein scale. A leftover
 * stored as exact grams (`partialGrams`) counts too, but only when the
 * yarn knows its grams-per-skein — without that there is no way to
 * express grams as a share of a skein.
 */
export function totalOwnedSkeins(yarn: {
  skeinCount?: number;
  partialSkein?: number;
  partialGrams?: number;
  gramsPerSkein?: number;
}): number {
  const gramsPerSkein = yarn.gramsPerSkein || 0;
  const fromGrams = gramsPerSkein > 0 ? (yarn.partialGrams || 0) / gramsPerSkein : 0;
  return (yarn.skeinCount || 0) + (yarn.partialSkein || 0) + fromGrams;
}

/**
 * Grams of leftover that `totalOwnedSkeins` could NOT fold into the
 * skein total (the yarn has no grams-per-skein). Cards print this as a
 * separate "+Ng" beside the skein number; a leftover that is already in
 * the total must not be printed again.
 */
export function uncountedPartialGrams(yarn: { partialGrams?: number; gramsPerSkein?: number }): number {
  return yarn.gramsPerSkein && yarn.gramsPerSkein > 0 ? 0 : yarn.partialGrams || 0;
}

/**
 * What one project yarn line takes from the stash, on the skein scale:
 * whole skeins + the ¼/½/¾ fraction + grams converted through
 * `gramsPerSkein` (grams are left out when that is unknown, same rule
 * as `totalOwnedSkeins`).
 */
export function claimedSkeins(
  y: { skeinsUsed?: number; partialSkeinUsed?: number; partialGramsUsed?: number },
  gramsPerSkein?: number,
): number {
  const fromGrams = gramsPerSkein && gramsPerSkein > 0 ? (y.partialGramsUsed || 0) / gramsPerSkein : 0;
  return (y.skeinsUsed || 0) + (y.partialSkeinUsed || 0) + fromGrams;
}

/**
 * Format the partial portion of a yarn — either a fraction glyph
 * (¼ / ½ / ¾) if stored as `partialSkein`, or a grams suffix (e.g.
 * "15g") if stored as `partialGrams`. The two are mutually exclusive;
 * if both are accidentally set the fraction wins. Returns the empty
 * string when there is no partial.
 */
export function formatPartialYarn(y: { partialSkein?: number; partialGrams?: number }): string {
  if (y.partialSkein) {
    if (y.partialSkein === 0.25) return '¼';
    if (y.partialSkein === 0.5) return '½';
    if (y.partialSkein === 0.75) return '¾';
    return String(y.partialSkein);
  }
  if (y.partialGrams && y.partialGrams > 0) return `${y.partialGrams}g`;
  return '';
}

/**
 * Format a skein quantity (which may be fractional) as a short human
 * string: whole number, fraction, or "N ½" / "N ¼" / "N ¾".
 *
 * When `gramsPerSkein` is provided AND the fractional remainder isn't
 * a clean ¼/½/¾ glyph, render the fraction as exact grams instead
 * of a decimal — e.g. "0.8 skein" with gramsPerSkein=25 becomes
 * "20g", and "1.2 skeins" with gramsPerSkein=100 becomes "1 + 20g".
 * Per Veronika 2026-05-18 (msg 4040 + 4001): "We need precise gram
 * value if we have the data."
 *
 * Without `gramsPerSkein` the function falls back to its original
 * decimal/fraction-glyph behaviour for legacy call sites.
 */
export function formatSkeinCount(value: number, gramsPerSkein?: number): string {
  if (!isFinite(value)) return '0';
  if (value === 0) return '0';
  const whole = Math.floor(value);
  const fracExact = value - whole;
  const frac = Math.round(fracExact * 100) / 100;
  const fracGlyph =
    frac === 0.25 ? '¼' :
    frac === 0.5 ? '½' :
    frac === 0.75 ? '¾' :
    '';
  if (fracGlyph) {
    if (whole === 0) return fracGlyph;
    return `${whole} ${fracGlyph}`;
  }
  // No clean fraction. Prefer grams output when we know the
  // grams-per-skein conversion factor.
  if (gramsPerSkein && gramsPerSkein > 0 && fracExact > 0.001) {
    const grams = Math.round(fracExact * gramsPerSkein * 10) / 10;
    if (whole === 0) return `${grams}g`;
    return `${whole} + ${grams}g`;
  }
  return String(Math.round(value * 100) / 100);
}
